// ════════════════════════════════════════════════════════════
// Client Inventory View
// Master sortable/filterable table of all clients.
// Click row → opens that client's Full Profile.
// Includes: search, AD/AM filters, service filter chips, hosting filter,
// status filter, sort by any column, CSV export, per-client history drawer.
// ════════════════════════════════════════════════════════════
import { useState, useMemo, useEffect } from 'react'
import { Search, ChevronUp, ChevronDown, Download, X, History, Plus } from 'lucide-react'
import { supabase } from './supabase'
import { useToast } from './ToastContext'
import { format } from 'date-fns'
import { FIELD_LABELS } from './clientChangeLog'

const safeFmt = (d, fmt) => {
  if (!d) return ''
  try {
    if (typeof d === 'string') {
      return format(new Date(d.includes('T') ? d : d + 'T12:00:00'), fmt)
    }
    return format(new Date(d), fmt)
  } catch { return '' }
}

const SERVICE_FILTERS = [
  { key: 'has_website', label: '🌐 Website' },
  { key: 'has_ads', label: '📣 Ads' },
  { key: 'has_seo', label: '🔍 SEO' },
  { key: 'has_social', label: '📱 Social' },
  { key: 'has_newsletters', label: '📧 Newsletters' },
  { key: 'has_blogs', label: '✍️ Blogs' },
]

const STATUS_FILTERS = [
  { value: 'active_open', label: 'Active + Onboarding' },
  { value: 'active', label: 'Active only' },
  { value: 'onboarding', label: 'Onboarding only' },
  { value: 'archived', label: 'Archived' },
  { value: 'all', label: 'All' },
]

export default function InventoryView({ clients, profiles, tasks, currentUser, onOpenClient, onAddClient }) {
  const [search, setSearch] = useState('')
  const [adFilter, setAdFilter] = useState('')
  const [amFilter, setAmFilter] = useState('')
  const [hostingFilter, setHostingFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('active_open')
  const [serviceFilters, setServiceFilters] = useState([])
  const [sortBy, setSortBy] = useState('name')
  const [sortDir, setSortDir] = useState('asc')
  const [historyClient, setHistoryClient] = useState(null)

  // Build lookup maps
  const profileById = useMemo(() => Object.fromEntries(profiles.map(p => [p.id, p])), [profiles])

  // Last-activity-per-client (most recent task updated_at)
  const lastActivityByClient = useMemo(() => {
    const map = new Map()
    for (const t of tasks) {
      if (!t.client_id) continue
      const ts = t.updated_at || t.created_at
      if (!ts) continue
      const prev = map.get(t.client_id)
      if (!prev || new Date(ts) > new Date(prev)) map.set(t.client_id, ts)
    }
    return map
  }, [tasks])

  // Unique hosting platforms in the dataset (for the filter dropdown)
  const hostingOptions = useMemo(() => {
    const set = new Set()
    clients.forEach(c => c.hosting_platform && set.add(c.hosting_platform))
    return [...set].sort()
  }, [clients])

  // Profile lists for the AD/AM dropdowns —
  // Include anyone who is EITHER set as AD/AM on at least one client,
  // OR has the relevant role. This way nobody gets missed because their
  // role was set wrong.
  const ads = useMemo(() => {
    const ids = new Set()
    clients.forEach(c => { if (c.account_director_id) ids.add(c.account_director_id) })
    profiles.forEach(p => { if (['ad', 'partner'].includes(p.role)) ids.add(p.id) })
    return profiles.filter(p => ids.has(p.id)).sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''))
  }, [profiles, clients])
  const ams = useMemo(() => {
    const ids = new Set()
    clients.forEach(c => { if (c.account_manager_id) ids.add(c.account_manager_id) })
    profiles.forEach(p => { if (['am', 'ad', 'partner'].includes(p.role)) ids.add(p.id) })
    return profiles.filter(p => ids.has(p.id)).sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''))
  }, [profiles, clients])

  // Apply filters
  const filtered = useMemo(() => {
    let rows = clients.slice()

    // Status
    if (statusFilter === 'active_open') rows = rows.filter(c => !c.archived_at)
    else if (statusFilter === 'active') rows = rows.filter(c => !c.archived_at && !c.is_onboarding)
    else if (statusFilter === 'onboarding') rows = rows.filter(c => !c.archived_at && c.is_onboarding)
    else if (statusFilter === 'archived') rows = rows.filter(c => c.archived_at)
    // 'all' = no filter

    // Search
    if (search) {
      const s = search.toLowerCase()
      rows = rows.filter(c =>
        (c.name || '').toLowerCase().includes(s)
        || (c.company_location || '').toLowerCase().includes(s)
        || (c.package_label || '').toLowerCase().includes(s)
        || (c.primary_contact_name || '').toLowerCase().includes(s)
      )
    }

    if (adFilter) rows = rows.filter(c => c.account_director_id === adFilter)
    if (amFilter) rows = rows.filter(c => c.account_manager_id === amFilter)
    if (hostingFilter) rows = rows.filter(c => c.hosting_platform === hostingFilter)

    // Service filters (AND — client must have ALL selected services)
    if (serviceFilters.length > 0) {
      rows = rows.filter(c => serviceFilters.every(k => c[k]))
    }

    return rows
  }, [clients, search, adFilter, amFilter, hostingFilter, statusFilter, serviceFilters])

  // Apply sort
  const sorted = useMemo(() => {
    const rows = filtered.slice()
    const dir = sortDir === 'asc' ? 1 : -1
    rows.sort((a, b) => {
      let av, bv
      switch (sortBy) {
        case 'name':
          av = (a.name || '').toLowerCase(); bv = (b.name || '').toLowerCase(); break
        case 'ad':
          av = (profileById[a.account_director_id]?.full_name || '').toLowerCase()
          bv = (profileById[b.account_director_id]?.full_name || '').toLowerCase(); break
        case 'am':
          av = (profileById[a.account_manager_id]?.full_name || '').toLowerCase()
          bv = (profileById[b.account_manager_id]?.full_name || '').toLowerCase(); break
        case 'hosting':
          av = (a.hosting_platform || '').toLowerCase(); bv = (b.hosting_platform || '').toLowerCase(); break
        case 'start_date':
          av = a.start_date || ''; bv = b.start_date || ''; break
        case 'last_activity':
          av = lastActivityByClient.get(a.id) || ''
          bv = lastActivityByClient.get(b.id) || ''; break
        default:
          av = ''; bv = ''
      }
      if (av < bv) return -1 * dir
      if (av > bv) return 1 * dir
      return 0
    })
    return rows
  }, [filtered, sortBy, sortDir, profileById, lastActivityByClient])

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortBy(col); setSortDir('asc') }
  }
  const toggleService = (k) => {
    setServiceFilters(prev => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k])
  }

  const clearFilters = () => {
    setSearch(''); setAdFilter(''); setAmFilter(''); setHostingFilter(''); setStatusFilter('active_open'); setServiceFilters([])
  }
  const hasActiveFilters = search || adFilter || amFilter || hostingFilter || serviceFilters.length > 0 || statusFilter !== 'active_open'

  const exportCsv = () => {
    const headers = ['Client', 'AD', 'AM', 'Status', 'Website', 'Ads', 'SEO', 'Social', 'Social/mo', 'Newsletters', 'NL/mo', 'Blogs', 'Blogs/mo', 'Hosting', 'Location', 'Start Date', 'Package Label']
    const rows = [headers]
    for (const c of sorted) {
      const ad = profileById[c.account_director_id]?.full_name || ''
      const am = profileById[c.account_manager_id]?.full_name || ''
      const status = c.archived_at ? 'Archived' : c.is_onboarding ? 'Onboarding' : 'Active'
      rows.push([
        c.name || '',
        ad, am, status,
        c.has_website ? 'yes' : '', c.has_ads ? 'yes' : '', c.has_seo ? 'yes' : '',
        c.has_social ? 'yes' : '', c.social_posts_per_month ?? '',
        c.has_newsletters ? 'yes' : '', c.newsletters_per_month ?? '',
        c.has_blogs ? 'yes' : '', c.blogs_per_month ?? '',
        c.hosting_platform || '', c.company_location || '',
        c.start_date || '', c.package_label || '',
      ])
    }
    const csv = rows.map(r => r.map(v => {
      const s = String(v ?? '')
      // Escape: wrap in quotes if it has commas, quotes, or newlines
      if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`
      return s
    }).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `client-inventory-${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a); a.click(); a.remove()
    URL.revokeObjectURL(url)
  }

  return (
    <div>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--navy)' }}>
          📋 Client Inventory <span style={{ fontSize: 14, color: 'var(--text-muted)', fontWeight: 400 }}>· {sorted.length} of {clients.length} {clients.length === 1 ? 'client' : 'clients'}</span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost btn-sm" onClick={exportCsv}><Download size={13} /> Export CSV</button>
          {onAddClient && <button className="btn btn-gold btn-sm" onClick={onAddClient}><Plus size={13} /> Add Client</button>}
        </div>
      </div>

      {/* Filters row */}
      <div style={{ background: '#fff', border: '1px solid var(--border-light)', borderRadius: 10, padding: 14, marginBottom: 14 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
          {/* Search */}
          <div style={{ position: 'relative' }}>
            <Search size={13} style={{ position: 'absolute', top: 10, left: 10, color: 'var(--text-muted)' }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, contact, package..." className="form-control" style={{ paddingLeft: 30, fontSize: 12.5 }} />
          </div>
          {/* AD */}
          <select className="form-control" value={adFilter} onChange={e => setAdFilter(e.target.value)} style={{ fontSize: 12.5 }}>
            <option value="">All Account Directors</option>
            {ads.map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
          </select>
          {/* AM */}
          <select className="form-control" value={amFilter} onChange={e => setAmFilter(e.target.value)} style={{ fontSize: 12.5 }}>
            <option value="">All Account Managers</option>
            {ams.map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
          </select>
          {/* Hosting */}
          <select className="form-control" value={hostingFilter} onChange={e => setHostingFilter(e.target.value)} style={{ fontSize: 12.5 }}>
            <option value="">All Hosting</option>
            {hostingOptions.map(h => <option key={h} value={h}>{h}</option>)}
          </select>
        </div>

        {/* Service & status filter chips */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {STATUS_FILTERS.map(s => (
              <button key={s.value} onClick={() => setStatusFilter(s.value)}
                className={`btn btn-sm ${statusFilter === s.value ? 'btn-gold' : 'btn-ghost'}`}>
                {s.label}
              </button>
            ))}
          </div>
          <div style={{ width: 1, height: 22, background: 'var(--border-light)', margin: '0 4px' }} />
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {SERVICE_FILTERS.map(s => (
              <button key={s.key} onClick={() => toggleService(s.key)}
                style={{
                  fontSize: 11.5, padding: '4px 10px', borderRadius: 50,
                  background: serviceFilters.includes(s.key) ? 'var(--navy)' : '#fff',
                  color: serviceFilters.includes(s.key) ? '#fff' : 'var(--text-primary)',
                  border: `1px solid ${serviceFilters.includes(s.key) ? 'var(--navy)' : 'var(--border-light)'}`,
                  cursor: 'pointer', fontWeight: serviceFilters.includes(s.key) ? 600 : 500,
                }}>
                {s.label}
              </button>
            ))}
          </div>
          {hasActiveFilters && (
            <button className="btn btn-ghost btn-sm" onClick={clearFilters} style={{ marginLeft: 'auto' }}>Clear filters</button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="card" style={{ overflow: 'auto' }}>
        <table className="task-table" style={{ width: '100%', minWidth: 900, fontSize: 12.5 }}>
          <thead>
            <tr>
              <SortHeader label="Client" col="name" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
              <SortHeader label="AD" col="ad" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
              <SortHeader label="AM" col="am" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
              <th>Services</th>
              <SortHeader label="Hosting" col="hosting" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
              <SortHeader label="Started" col="start_date" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
              <SortHeader label="Last Activity" col="last_activity" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
              <th style={{ width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontStyle: 'italic' }}>No clients match these filters</td></tr>
            ) : sorted.map(c => {
              const ad = profileById[c.account_director_id]
              const am = profileById[c.account_manager_id]
              const lastAct = lastActivityByClient.get(c.id)
              const isArchived = !!c.archived_at
              const isOnboarding = c.is_onboarding && !c.archived_at
              return (
                <tr key={c.id} style={{ cursor: 'pointer', opacity: isArchived ? 0.6 : 1 }}
                  onClick={e => {
                    if (['BUTTON', 'SVG', 'PATH'].includes(e.target.tagName?.toUpperCase())) return
                    onOpenClient && onOpenClient(c)
                  }}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{c.name}</div>
                    {(isArchived || isOnboarding) && (
                      <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 50, marginTop: 2, display: 'inline-block',
                        background: isArchived ? '#f5f5f5' : 'var(--mauve)',
                        color: isArchived ? 'var(--text-muted)' : '#fff' }}>
                        {isArchived ? 'Archived' : 'Onboarding'}
                      </span>
                    )}
                  </td>
                  <td>{ad?.full_name || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                  <td>{am?.full_name || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, maxWidth: 280 }}>
                      {c.has_website && <ServiceMicroChip icon="🌐" />}
                      {c.has_ads && <ServiceMicroChip icon="📣" />}
                      {c.has_seo && <ServiceMicroChip icon="🔍" />}
                      {c.has_social && <ServiceMicroChip icon="📱" count={c.social_posts_per_month} />}
                      {c.has_newsletters && <ServiceMicroChip icon="📧" count={c.newsletters_per_month} />}
                      {c.has_blogs && <ServiceMicroChip icon="✍️" count={c.blogs_per_month} />}
                      {![c.has_website, c.has_ads, c.has_seo, c.has_social, c.has_newsletters, c.has_blogs].some(Boolean) && (
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>none set</span>
                      )}
                    </div>
                  </td>
                  <td>{c.hosting_platform || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                  <td>{c.start_date ? safeFmt(c.start_date, 'MMM yyyy') : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                  <td>{lastAct ? safeFmt(lastAct, 'MMM d') : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                  <td onClick={e => e.stopPropagation()} style={{ textAlign: 'center' }}>
                    <button className="btn-icon" title="View change history" onClick={() => setHistoryClient(c)}>
                      <History size={13} />
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {historyClient && (
        <HistoryDrawer client={historyClient} profiles={profiles} onClose={() => setHistoryClient(null)} />
      )}
    </div>
  )
}

function SortHeader({ label, col, sortBy, sortDir, onClick }) {
  const active = sortBy === col
  return (
    <th onClick={() => onClick(col)} style={{ cursor: 'pointer', userSelect: 'none' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
        {label}
        {active && (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
      </span>
    </th>
  )
}

function ServiceMicroChip({ icon, count }) {
  return (
    <span style={{
      fontSize: 11, padding: '2px 6px', borderRadius: 50,
      background: 'var(--cream)', border: '1px solid var(--border-light)',
      whiteSpace: 'nowrap',
    }}>
      {icon}{count != null ? ` ${count}` : ''}
    </span>
  )
}

// ────────────────────────────────────────────────────────────
// History drawer — shows client_changes_log entries
// ────────────────────────────────────────────────────────────
function HistoryDrawer({ client, profiles, onClose }) {
  const [entries, setEntries] = useState(null)
  useEffect(() => {
    supabase.from('client_changes_log').select('*').eq('client_id', client.id).order('changed_at', { ascending: false }).limit(100)
      .then(({ data }) => setEntries(data || []))
  }, [client.id])

  const profileById = Object.fromEntries(profiles.map(p => [p.id, p]))

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 90, display: 'flex', justifyContent: 'flex-end' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(26,43,74,0.3)' }} onClick={onClose} />
      <div style={{ position: 'relative', width: 480, background: '#fff', boxShadow: 'var(--shadow-lg)', display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ background: 'var(--navy)', padding: '18px 22px', color: '#fff', flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Change History</div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 17, marginTop: 4 }}>{client.name}</div>
          </div>
          <button className="btn-icon" style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff' }} onClick={onClose}><X size={16} /></button>
        </div>
        <div style={{ padding: '18px 22px', flex: 1, overflowY: 'auto' }}>
          {entries === null ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading...</div>
          ) : entries.length === 0 ? (
            <div className="empty-state" style={{ padding: 30 }}>
              <History size={32} style={{ color: 'var(--text-muted)', marginBottom: 8 }} />
              <h3>No changes logged yet</h3>
              <p>Future changes to this client's services, team, or package will be tracked here.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {entries.map(e => {
                const who = profileById[e.changed_by]?.full_name || 'Someone'
                const fieldLabel = FIELD_LABELS[e.field_name] || e.field_name
                return (
                  <div key={e.id} className="card" style={{ padding: 0 }}>
                    <div className="card-body" style={{ padding: '10px 14px' }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>
                        {fieldLabel}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {e.old_value ? <span style={{ textDecoration: 'line-through', color: 'var(--text-muted)' }}>{e.old_value}</span> : <em style={{ color: 'var(--text-muted)' }}>not set</em>}
                        {' → '}
                        <strong style={{ color: 'var(--text-primary)' }}>{e.new_value || <em style={{ color: 'var(--text-muted)' }}>not set</em>}</strong>
                      </div>
                      <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 6 }}>
                        {who} · {safeFmt(e.changed_at, 'MMM d, yyyy h:mm a')}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}